import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Stack,
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Chip,
  TextField,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { getPlatformUsage } from "api/platformApi";
import { unwrapList } from "api/unwrap";

/**
 * /platform/usage — cross-tenant usage rollup. Sorted over-limit first then by
 * fleet size so the obvious upgrade conversations surface at the top.
 */

const csvEscape = (v) => {
  if (v === null || v === undefined) return "";
  const s = String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const exportCsv = (rows) => {
  const header = [
    "tenantId", "name", "subdomain", "plan",
    "drivers", "driverLimit", "overDriverLimit",
    "vehicles", "vehicleLimit", "overVehicleLimit",
    "customerCompanies", "customerCompanyLimit", "overCustomerCompanyLimit",
    "ridesThisMonth", "rideQuota", "rideOverage",
  ];
  const body = rows.map((r) => header.map((h) => csvEscape(r[h])).join(","));
  const csv = [header.join(","), ...body].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `platform-usage-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

export const PlatformUsagePage = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getPlatformUsage();
      setRows(unwrapList(res));
    } catch (e) {
      setError(e?.message || "Failed to load usage");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      `${r.name || ""} ${r.subdomain || ""} ${r.plan || ""}`.toLowerCase().includes(q),
    );
  }, [rows, filter]);

  const totals = useMemo(() => {
    return rows.reduce(
      (a, r) => ({
        drivers: a.drivers + (r.drivers || 0),
        vehicles: a.vehicles + (r.vehicles || 0),
        rides: a.rides + (r.ridesThisMonth || 0),
        overDriver: a.overDriver + (r.overDriverLimit ? 1 : 0),
        overVehicle: a.overVehicle + (r.overVehicleLimit ? 1 : 0),
        overCompany: a.overCompany + (r.overCustomerCompanyLimit ? 1 : 0),
        overage: a.overage + (r.rideOverage || 0),
      }),
      { drivers: 0, vehicles: 0, rides: 0, overDriver: 0, overVehicle: 0, overCompany: 0, overage: 0 },
    );
  }, [rows]);

  return (
    <Stack
      spacing={3}
      sx={{
        p: { xs: 2, md: 3 },
        backgroundColor: "#f8fafc",
        height: "calc(100vh - 80px)",
        overflowY: "auto",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: "#0f172a" }}>
            Platform — Usage
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Drivers vs plan limit + monthly ride volume across every tenant. Over-limit tenants are surfaced first.
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button size="small" variant="outlined" startIcon={<FileDownloadIcon />} onClick={() => exportCsv(filtered)} disabled={loading || filtered.length === 0}>
            Export CSV
          </Button>
          <Button size="small" variant="outlined" startIcon={<RefreshIcon />} onClick={load} disabled={loading}>
            Refresh
          </Button>
        </Box>
      </Box>

      {error ? <Alert severity="error">{error}</Alert> : null}

      {loading && rows.length === 0 ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : rows.length === 0 ? (
        <Card variant="outlined">
          <CardContent>
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", py: 4 }}>
              No usage data. The platform admin endpoints are gated behind {`platform.admin.enabled=true`} + SUPER_ADMIN.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <>
          <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
            <Chip label={`${totals.overDriver} over driver limit`} color={totals.overDriver > 0 ? "error" : "default"} />
            <Chip label={`${totals.overVehicle} over vehicle limit`} color={totals.overVehicle > 0 ? "error" : "default"} />
            <Chip label={`${totals.overCompany} over company limit`} color={totals.overCompany > 0 ? "error" : "default"} />
            <Chip label={`+${totals.overage} billable overage rides MTD`} color={totals.overage > 0 ? "warning" : "default"} />
            <Chip label={`${totals.drivers} drivers`} />
            <Chip label={`${totals.vehicles} vehicles`} />
            <Chip label={`${totals.rides} rides this month`} />
          </Box>

          <TextField
            size="small"
            placeholder="Filter by name / subdomain / plan"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            sx={{ maxWidth: 480 }}
          />

          <TableContainer component={Paper} elevation={0} sx={{ border: "1px solid #e2e8f0" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Tenant</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Plan</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Drivers</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Vehicles</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Rides MTD</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Customers</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map((r) => (
                  <TableRow key={r.tenantId} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{r.name || "—"}</Typography>
                      <Typography variant="caption" color="text.secondary">{r.subdomain || ""}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={r.plan || "—"}
                        sx={{ height: 22, textTransform: "capitalize" }}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 700, color: r.overDriverLimit ? "#dc2626" : "inherit" }}
                        >
                          {r.drivers}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          / {r.driverLimit > 0 ? r.driverLimit : "∞"}
                        </Typography>
                        {r.overDriverLimit ? (
                          <Chip
                            size="small"
                            label="upgrade"
                            sx={{ height: 18, backgroundColor: "#dc2626", color: "#fff", fontWeight: 700 }}
                          />
                        ) : null}
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 700, color: r.overVehicleLimit ? "#dc2626" : "inherit" }}
                        >
                          {r.vehicles}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          / {r.vehicleLimit > 0 ? r.vehicleLimit : "∞"}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 700, color: r.rideOverage > 0 ? "#dc2626" : "inherit" }}
                        >
                          {r.ridesThisMonth}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          / {r.rideQuota > 0 ? r.rideQuota : "∞"}
                        </Typography>
                        {r.rideOverage > 0 ? (
                          <Chip
                            size="small"
                            label={`+${r.rideOverage}`}
                            sx={{ height: 18, backgroundColor: "#f59e0b", color: "#fff", fontWeight: 700 }}
                          />
                        ) : null}
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 700, color: r.overCustomerCompanyLimit ? "#dc2626" : "inherit" }}
                        >
                          {r.customerCompanies}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          / {r.customerCompanyLimit > 0 ? r.customerCompanyLimit : "∞"}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Stack>
  );
};

export default PlatformUsagePage;
